/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import Box from '@material-ui/core/Box';
import {translate} from '@ion/core/src/locale/locale';
import {Crumb} from '@ion/core/src/types/entities';
import {PrimaryButton} from '../../components/buttons/PrimaryButton';
import {TertiaryButton} from '../../components/buttons/TertiaryButton';
import Slider from '../../components/slider/Slider';
import TextField from '../../components/textfield/TextField';
import {dlsColor, dlsLayout} from '../../theme/theme';
import {LabelTypography} from '../../typography/LabelTypography';
import SearchAddress from './SearchAddress';

type SearchProps = {
  view: __esri.MapView;
  isVisible: boolean;
  radius?: number;
  handleRadiusInput?: Function;
  drawingComplete: boolean;
  handleRadiusFromPanel?: Function;
  handleCompleteDrawing: Function;
  handleSetSelectedMapPoint?: Function;
  crumbs: Crumb[];
  selectCrumbs: Function;
  geofenceType: string;
  mapClickHandler?: any;
  handleBack?: Function;
  useHereApi?: boolean;
  isPhone?: boolean;
  toggleShowBoundaryPanel?: Function;
};

const useStyles = makeStyles((theme) => ({
  container : {
    maxWidth   : ({isPhone}: {isPhone: boolean}) => isPhone ? '100%': 285,
    maxHeight  : '600px',
    margin     : '10px',
    alignItems : 'center'
  },
  actionButtonContainer : {
    display        : 'flex',
    flexDirection  : 'row',
    justifyContent : 'space-between',
    alignItems     : 'center'
  },
  cancelButton : {
    textAlign : 'center',
    width     : '50%'
  },
  radialInput : {
    '&&' : {
      padding : '0 !important'
    }
  },
  distanceField : {
    width                          : 60,
    marginLeft                     : dlsLayout('margins'),
    display                        : 'flex',
    alignSelf                      : 'flex-end',
    [theme.breakpoints.down('md')] : {
      marginTop    : 14,
      marginBottom : -26
    },
    [theme.breakpoints.up('md')] : {
      marginTop    : 8,
      marginBottom : -26
    }
  },
  radialInputUnderline : {
    '&::after' : {
      borderColor : dlsColor('caBlue')
    },
    '&::before' : {
      borderColor       : `${dlsColor('grey3')} !important`,
      borderBottomWidth : '1px !important'
    }
  },
  boxContainer : {
    display       : 'flex',
    flexDirection : 'column-reverse',
    marginBottom  : '0px'
  },
  sliderBox : {
    flexGrow : 1
  },
  slider : {
    maxWidth : ({isPhone}: {isPhone: boolean}) => isPhone ? '100%': 270
  },
  button : {
    marginBottom : '5px',
    width        : '90%'
  }
}));

const radiusMarks = [
  {
    value : 0,
    label : '0'
  },
  {
    value : 50,
    label : '50'
  },
  {
    value : 100,
    label : '100'
  },
  {
    value : 200,
    label : '200'
  },
  {
    value : 300,
    label : '300'
  }
];

export function GeofenceSearch({
  view,
  isVisible,
  radius,
  handleRadiusInput,
  drawingComplete,
  handleRadiusFromPanel,
  handleCompleteDrawing,
  selectCrumbs,
  geofenceType,
  crumbs,
  mapClickHandler,
  handleBack,
  toggleShowBoundaryPanel,
  handleSetSelectedMapPoint,
  isPhone,
  useHereApi = false
}: SearchProps) {
  const classes = useStyles({isPhone});
  return (
    <div className={classes.container}>
      <SearchAddress useHereApi={useHereApi}
        mapView={view}
        isSave
        geofenceType={geofenceType}
        isVisible={isVisible}
        handleSetSelectedMapPoint={handleSetSelectedMapPoint} />
      {geofenceType === translate('maps:geofences.radial') && (
        <Box className={classes.boxContainer}>
          <Box className={classes.sliderBox}>
            <LabelTypography>{translate('maps:geofences.radius')}</LabelTypography>
            <Slider className={classes.slider}
              step={1}
              max={300}
              value={radius}
              marks={radiusMarks}
              onSlide={function(event, newValue) {
                handleRadiusFromPanel(newValue as number);
              }} />
          </Box>
          <Box className={classes.distanceField}>
            <TextField value={radius}
              onChange={handleRadiusInput}
              InputProps={{
                classes : {
                  input     : classes.radialInput,
                  underline : classes.radialInputUnderline
                },
                endAdornment : <span>{translate('maps:geofences.meters')}</span>
              }}
              inputProps={{
                maxLength : 7 // preventing user from adding unreal radius
              }} />
          </Box>
        </Box>)}
      <div className={classes.actionButtonContainer}>
        <div className={classes.cancelButton}>
          <TertiaryButton noUnderline
            onClick={function() {
              toggleShowBoundaryPanel?.();
              handleBack?.();
              selectCrumbs(0, crumbs);
              mapClickHandler?.remove();
            }}>
            {translate('maps:geofences.cancel')}
          </TertiaryButton>
        </div>
        {geofenceType === translate('maps:geofences.polygon') ? (
          <PrimaryButton onClick={handleCompleteDrawing}
            disabled={!drawingComplete}
            className={classes.button}
            fullWidth>
            {`${translate('maps:geofences.next')}`}
          </PrimaryButton>
        ) : (
          <PrimaryButton onClick={handleCompleteDrawing}
            disabled={!drawingComplete}
            className={classes.button}
            fullWidth>
            {`${translate('maps:geofences.next')}`}
          </PrimaryButton>
        )}
      </div>
    </div>
  );
}
